import type { EventType } from '@cocoonspace/sdk-js/v2/types/eventType'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useGetVenue } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useLingui } from '@lingui/react/macro'
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon } from 'lucide-react'

import { formatPersonName } from '~/utils/format-person-name'
import { OnsiteBadge, RemoteBadge } from '../../event-types/event-type-badges'
import { useAnswerCard } from './provider'
import type { BookingRequestAnswerFormData } from '.'

const VenueSection = ({ venueId }: { venueId: string }) => {
	const { t } = useLingui()

	const { isLoading, isSuccess, data } = useGetVenue(venueId, undefined, {
		query: { select: (data) => data.data.address },
	})

	return (
		<div className='text-muted-foreground flex items-start gap-1'>
			<MapPinIcon className='align-baseline' />

			<div className='grid'>
				{isLoading && <span>{t`Chargement`}</span>}

				{isSuccess && (
					<div className='flex flex-col gap-1 leading-none'>
						<span className='truncate'>{data.address1}</span>
						<span className='truncate'>{`${data.postal_code || ''} ${data.city || ''}`}</span>
					</div>
				)}
			</div>
		</div>
	)
}

export const EventTypeBadge = ({
	eventType,
	className,
}: {
	className?: string
	eventType: Pick<EventType, 'location'>
}) => {
	if (eventType.location?.venue_id) {
		return <OnsiteBadge className={className} />
	}

	if (eventType.location?.type === 'remote') {
		return <RemoteBadge className={className} />
	}

	return null
}

const OwnerSection = ({ eventType }: { eventType: EventType }) => {
	const displayName = useMemo(() => {
		const { firstName, lastName } = eventType.user || {}
		return formatPersonName(firstName, lastName)
	}, [eventType.user])

	return (
		<div className='text-muted-foreground flex items-center gap-1'>
			<UserIcon />
			<span className='truncate'>{displayName}</span>
		</div>
	)
}

const DurationSection = ({ eventType }: { eventType: EventType }) => {
	const duration = useMemo(() => {
		return eventType.duration < 60
			? `${eventType.duration} min`
			: dateTimeUtils
					.duration(eventType.duration, 'minutes')
					.format('H[h]mm')
					.replace('h00', 'h')
	}, [eventType.duration])

	return (
		<div className='text-muted-foreground flex items-center gap-1'>
			<ClockIcon />
			{duration}
		</div>
	)
}

const TimeSlotRecapSection = () => {
	const { t } = useLingui()

	const form = useFormContext<BookingRequestAnswerFormData>()
	const day = form.watch('day')

	const start = form.watch('start')

	const formattedDay = useMemo(() => {
		return dateTimeUtils(day).customFormat('date')
	}, [day])

	if (!day || !start) return null

	return (
		<div className='text-muted-foreground flex items-center gap-1'>
			<CalendarIcon />
			{t`Le ${formattedDay}, à ${start}`}
		</div>
	)
}

export const EventRecap = () => {
	const { eventType } = useAnswerCard()

	return (
		<div className='col-span-1 flex flex-col justify-between gap-4 p-4 md:w-[300px]'>
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-2 leading-none'>
					<EventTypeBadge
						eventType={eventType}
						className='mb-2'
					/>

					<div className='flex flex-col gap-2 text-2xl font-bold'>
						{eventType.title}
					</div>
				</div>

				<div className='flex flex-col gap-2'>{eventType.description}</div>
			</div>

			<div className='flex flex-col gap-1'>
				<OwnerSection eventType={eventType} />
				<TimeSlotRecapSection />
				<DurationSection eventType={eventType} />

				{eventType.location?.venue_id && (
					<VenueSection venueId={eventType.location.venue_id} />
				)}
			</div>
		</div>
	)
}
